/**
 * The circles first-name gate (PPL-508).
 *
 * Circles are the personal half of the product — members render by firstName —
 * so the server asks a joiner or creator for exactly one thing: a first name.
 * `joinCircleRequestEurW` / `createCircleEurW` refuse with
 * failed-precondition, a plain sentence, and `details.missing: ['firstName']`.
 *
 * Recognising that one refusal lives here so every circle surface offers the
 * same way out. The sentence the user reads is always the server's — this
 * module never invents copy.
 */

/** Where the gate sends the user. */
export const PROFILE_EDIT_PATH = '/profile/edit';

/** The action label next to the message. A static label, not contextual copy. */
export const FIRST_NAME_ACTION_LABEL = 'add your first name';

type ErrorLike = {
  message?: unknown;
  details?: unknown;
};

function missingFirstName(details: unknown): boolean {
  const missing = (details as { missing?: unknown } | null)?.missing;
  return Array.isArray(missing) && missing.includes('firstName');
}

/**
 * The gate's message when `error` is the first-name refusal, else null.
 *
 * Detection is by `details.missing` first and the message text only as a
 * fallback, so a copy edit on the server never silently drops the action.
 */
export function firstNameGateMessage(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const e = error as ErrorLike;
  const message = typeof e.message === 'string' ? e.message : '';
  const matched = missingFirstName(e.details) || /first name/i.test(message);
  return matched && message ? message : null;
}
